import { type PushProduct, productBySku, triedFrom } from "./data"
import { LAST_MEETING, TODAY, addDays, daysBetween, shortDate } from "./dates"
import { type BasePoint, type NumberedAction } from "./salesSeries"
import { SHEET_BATCH_ROWS, type SheetBatchRow } from "./sheetData"
import { channelOutPerDay, hash } from "./stock"

// Batches for Expiring soon, from the sheet's "Expiry-need push" list. A batch
// is one product with one expiry date, summed over every warehouse that holds
// it. Quantities today and at the Sep 17 meeting are the sheet's. The sheet
// has no history before Sep 17, so the quantity when the batch joined the
// push list (18 months before it expires) and the weekly line up to Sep 17
// are made up.

const PUSH_WINDOW_MONTHS = 18

export interface BatchSite {
  warehouse: string
  qty: number
  outPerDay: number | null // the whole product through this warehouse's channel, every batch
}

export interface BatchPoint extends BasePoint {
  qty: number
}

export interface Batch {
  id: string // Lixibox ID@expiry date
  product: PushProduct
  expires: string // ISO date
  monthsLeft: number
  qty: number
  costPerUnit: number
  startQty: number
  startedOn: string // the day it joined the push list
  lastMeetingQty: number | null // null when it was not on the Sep 17 list
  sites: BatchSite[]
  points: BatchPoint[] // one per week from startedOn to today
}

// The sheet's warehouse names, and which channel each one serves.
const SHEET_WAREHOUSES: Record<string, { name: string; channel: number | null }> = {
  "Boxme MKP": { name: "Boxme MKP", channel: 0 },
  "Boxme ECOM": { name: "Boxme Ecom", channel: 1 },
  Viettel: { name: "Viettel Post", channel: null }, // the sheet does not say which Viettel warehouse
}

function wobble(seed: number, step: number) {
  const x = Math.sin(seed * 12.9898 + step * 78.233) * 43758.5453
  return (x - Math.floor(x) - 0.5) * 0.8
}

function monthsBefore(iso: string, months: number) {
  const d = new Date(iso + "T00:00:00Z")
  d.setUTCMonth(d.getUTCMonth() - months)
  return d.toISOString().slice(0, 10)
}

// Weekly dates from `from` to `to`, with `to` always the last one.
function weeklyDates(from: string, to: string) {
  const dates = Array.from({ length: Math.floor(daysBetween(from, to) / 7) + 1 }, (_, k) => addDays(from, k * 7))
  if (dates[dates.length - 1] !== to) dates.push(to)
  return dates
}

// Quantity left on each date, going from `startQty` to `endQty` in uneven
// weekly steps.
function path(dates: string[], startQty: number, endQty: number, seed: number) {
  const weights = dates.slice(1).map((_, k) => Math.max(0.2, 1 + wobble(seed, k)))
  const total = weights.reduce((a, b) => a + b, 0)
  let left = startQty
  return dates.map((_, i) => {
    if (i > 0) left -= ((startQty - endQty) * weights[i - 1]) / total
    return i === dates.length - 1 ? endQty : Math.round(left)
  })
}

function batchOf(rows: SheetBatchRow[]): Batch {
  const { lixibox, expires } = rows[0]
  const product = productBySku(lixibox)!
  const qty = rows.reduce((sum, r) => sum + r.qty, 0)
  const matched = rows.some((r) => r.qtySep17 !== null)
  const lastMeetingQty = matched ? rows.reduce((sum, r) => sum + (r.qtySep17 ?? 0), 0) : null

  const startedOn = monthsBefore(expires, PUSH_WINDOW_MONTHS)
  const seed = hash(lixibox + expires)
  const weeksIn = daysBetween(startedOn, TODAY) / 7
  const startQty = Math.round(Math.max(qty, lastMeetingQty ?? 0) * (1 + weeksIn * (0.01 + (seed % 3) / 100)))

  // Made-up line from startedOn to the last meeting, then the sheet's two
  // figures joined in weekly steps.
  let dates: string[]
  let qtys: number[]
  if (lastMeetingQty !== null && startedOn < LAST_MEETING) {
    const before = weeklyDates(startedOn, LAST_MEETING)
    const after = weeklyDates(LAST_MEETING, TODAY).slice(1)
    dates = [...before, ...after]
    qtys = [...path(before, startQty, lastMeetingQty, seed), ...path([LAST_MEETING, ...after], lastMeetingQty, qty, seed + 1).slice(1)]
  } else {
    dates = weeklyDates(startedOn, TODAY)
    qtys = path(dates, startQty, qty, seed)
  }

  const tried = [...new Set(rows.flatMap((r) => triedFrom(r.actionSep17)))]
  const actions: NumberedAction[] = tried.map((type, i) => ({ type, date: LAST_MEETING, n: i + 1 }))

  return {
    id: `${lixibox}@${expires}`,
    product,
    expires,
    monthsLeft: Math.round(daysBetween(TODAY, expires) / 30.44),
    qty,
    costPerUnit: rows[0].cost,
    startQty,
    startedOn,
    lastMeetingQty,
    sites: rows.map((r) => {
      const where = SHEET_WAREHOUSES[r.warehouse] ?? { name: r.warehouse, channel: null }
      return { warehouse: where.name, qty: r.qty, outPerDay: where.channel === null ? null : channelOutPerDay(product, where.channel) }
    }),
    points: dates.map((date, i) => ({
      i,
      tick: shortDate(date),
      title: date === TODAY ? "Today" : `Week of ${shortDate(date)}`,
      actions: actions.filter((a) => a.date >= date && (i === dates.length - 1 || a.date < dates[i + 1])),
      qty: qtys[i],
    })),
  }
}

const groups = new Map<string, SheetBatchRow[]>()
for (const row of SHEET_BATCH_ROWS) {
  const key = `${row.lixibox}@${row.expires}`
  groups.set(key, [...(groups.get(key) ?? []), row])
}

export const BATCHES: Batch[] = [...groups.values()].map(batchOf)

export const batchValue = (batch: Batch) => batch.qty * batch.costPerUnit

export const otherBatchesOf = (batch: Batch) =>
  BATCHES.filter((b) => b.product.sku === batch.product.sku && b.id !== batch.id)
